import { useEffect, useState } from "react";
import { getVoterGuide, getPriorities } from "@/lib/api";
import { Printer } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useT } from "@/lib/t";

export default function VoterGuide() {
  const { lang } = useLang();
  const t = useT();
  const [guide, setGuide] = useState(null);
  const [priorities, setPriorities] = useState([]);

  useEffect(() => {
    getVoterGuide(lang).then(setGuide);
    getPriorities(lang).then(setPriorities);
  }, [lang]);

  if (!guide) return <div className="max-w-4xl mx-auto px-6 py-24">{t("common.loading")}</div>;

  // Empty-state — guide loaded but no title/intro yet.
  if (!guide.title) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24" data-testid="voter-guide-empty">
        <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-3">{t("voter_guide.eyebrow")}</div>
        <h1 className="font-serif text-4xl text-ccc-plum mb-4">{t("voter_guide.hero_title")}</h1>
        <p className="text-stone-700">{t("voter_guide.empty")}</p>
      </div>
    );
  }

  const map = Object.fromEntries(priorities.map(p => [p.slug, p]));

  return (
    <div data-testid="voter-guide">
      <section className="bg-ccc-plum text-ccc-ivory py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-3">{t("voter_guide.eyebrow")}</div>
            <h1 className="font-serif text-5xl mb-2">{guide.title}</h1>
            <div className="text-stone-300">{guide.cycle}</div>
          </div>
          <button onClick={() => window.print()} data-testid="voter-print"
            className="border border-ccc-gold text-ccc-gold hover:bg-ccc-gold hover:text-ccc-plum px-5 py-3 inline-flex items-center gap-2 uppercase text-sm tracking-wider font-semibold transition-colors">
            <Printer size={14}/> {lang === "es" ? "Imprimir para la Parroquia" : "Print for Parish"}
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16">
        <p className="font-serif text-xl text-ccc-charcoal leading-relaxed">{guide.intro}</p>
        <div className="gilded-divider my-10"/>
        <div className="space-y-8">
          {guide.sections?.map((s, i) => {
            const p = map[s.priority_slug];
            if (!p) return null;
            return (
              <article key={i} data-testid={`vg-${s.priority_slug}`} className="grid md:grid-cols-6 gap-6 border-b border-stone-200 pb-8">
                <div className="md:col-span-1 font-serif text-5xl text-ccc-gold">{p.order}</div>
                <div className="md:col-span-5">
                  <h2 className="font-serif text-3xl text-ccc-plum mb-2">{p.title}</h2>
                  <p className="text-sm text-stone-600 mb-3 italic">{p.summary}</p>
                  <p className="text-stone-800 leading-relaxed">{s.guidance}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
